import { useEffect, useRef, useState } from 'react';

import { WalletImageInfo } from '../../../../models/interfaces';
import { ImageRule, validateImage } from './walletUtils';

interface WalletImageFieldProps {
    label: string;
    platform: 'iphone' | 'android';
    purpose: string;
    rules: string;
    badge?: { text: string; className: string };
    rule: ImageRule;
    savedUrl: string | null;           // imagen guardada (object URL)
    savedInfo?: WalletImageInfo | null;
    file: File | null;                 // archivo nuevo pendiente de guardar
    onChange: (file: File | null) => void;
    circle?: boolean;                  // Google muestra el logo recortado en círculo
    wide?: boolean;                    // banners: vista previa a lo ancho
    note?: React.ReactNode;
    disabled?: boolean;
}

// Tarjeta para subir una imagen de la tarjeta digital: explica para qué sirve, valida y muestra la vista previa
export const WalletImageField = ({
    label, platform, purpose, rules, badge, rule, savedUrl, savedInfo, file, onChange, circle, wide, note, disabled
}: WalletImageFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState('');
    const [pendingUrl, setPendingUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!file) { setPendingUrl(null); return; }
        const url = URL.createObjectURL(file);
        setPendingUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const pick = async (selected: File | undefined) => {
        if (inputRef.current) inputRef.current.value = '';
        if (!selected) return;
        const message = await validateImage(selected, rule);
        setError(message ?? '');
        if (!message) onChange(selected);
    };

    const preview = pendingUrl ?? savedUrl;
    const previewBox: React.CSSProperties = wide
        ? { width: '100%', aspectRatio: rule.kind === 'exact' ? `${rule.width} / ${rule.height}` : '16 / 6', maxHeight: 150 }
        : { width: 88, height: 88, flexShrink: 0 };

    return (
        <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                <div className="fw-semibold">
                    <i className={`bi ${platform === 'iphone' ? 'bi-apple' : 'bi-android2'} me-1 text-muted`} />
                    {label}
                </div>
                {badge && <span className={`badge rounded-pill ${badge.className}`}>{badge.text}</span>}
            </div>
            <div className="small text-muted mb-2">{purpose}</div>

            <div className={`d-flex gap-3 ${wide ? 'flex-column' : 'align-items-center'} mb-2`}>
                <div
                    className="border bg-light d-flex align-items-center justify-content-center overflow-hidden"
                    style={{ ...previewBox, borderRadius: circle ? '50%' : 8, backgroundImage: 'repeating-conic-gradient(#eee 0 25%, #fff 0 50%)', backgroundSize: '16px 16px' }}
                >
                    {preview
                        ? <img src={preview} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: wide ? 'cover' : 'contain', width: wide ? '100%' : undefined, height: wide ? '100%' : undefined }} />
                        : <i className="bi bi-image text-muted fs-3" />}
                </div>
                <div className="small">
                    {file ? (
                        <div className="text-primary"><i className="bi bi-arrow-up-circle me-1" />Nueva: {file.name} <span className="text-muted">(sin guardar)</span></div>
                    ) : savedInfo ? (
                        <div className="text-success"><i className="bi bi-check-circle me-1" />Guardada · {savedInfo.ancho}×{savedInfo.alto} px</div>
                    ) : savedUrl ? (
                        <div className="text-success"><i className="bi bi-check-circle me-1" />Guardada</div>
                    ) : (
                        <div className="text-muted">Sin imagen</div>
                    )}
                    <div className="text-muted mt-1">{rules}</div>
                </div>
            </div>

            {note && <div className="small text-muted mb-2">{note}</div>}
            {error && <div className="small text-danger mb-2"><i className="bi bi-exclamation-circle me-1" />{error}</div>}

            <div className="mt-auto d-flex gap-2">
                <input ref={inputRef} type="file" accept="image/png,.png" className="d-none" disabled={disabled}
                    onChange={(e) => pick(e.target.files?.[0])} />
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={() => inputRef.current?.click()}>
                    <i className="bi bi-upload me-1" />{savedUrl || file ? 'Cambiar' : 'Subir'}
                </button>
                {file && (
                    <button type="button" className="btn btn-sm btn-link text-muted" disabled={disabled} onClick={() => { onChange(null); setError(''); }}>
                        Descartar
                    </button>
                )}
            </div>
        </div>
    );
};
